<template>
    <div>
        <div class="w-full flex flex-column align-items-center mt-5" style="color: var(--theme-font-color);">
            
            <div style="height: 2.9rem;"></div>
            <span style="color: var(--theme-font-color); font-size: var(--register-maintitle-font-size); font-weight: var(--register-maintitle-font-weight);">Access Sharing Confirmation</span>

            <div v-if="!isConfirmed" class="w-full flex flex-row justify-content-center">
                <div v-if="!errorConfirmation" style="height: 65vh;" class="flex align-items-center justify-content-center">
                    <LoadingSpinner/>
                </div>
                <div v-else class="flex flex-column align-items-center justify-content-center mt-5" style="font-size: var(--text-font-size); font-weight: var(--text-font-weight); width: 42%; min-width: 32rem !important;">
                    <SvgError class="mb-4" className="registerconfirm-svg" color="var(--mydanger-color)"/>
                    <span v-if="confirmationErrorMessage" class="text-center" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">{{ confirmationErrorMessage }}</span>

                    <span style="font-size: var(--title-font-size); font-weight: var(--text-font-weight); gap: 0.4rem;" class="text-center mt-2 align-items-center">
                        Note that you can manually give access to the user for the serial number through the 'Authorized Users' section in
                        <router-link unstyled :to="{ path: '/login'}" style="text-decoration: none;">
                            <span id="clickable-text" class="cursor-pointer">Softron License Manager</span>
                        </router-link>
                        .
                    </span>
                </div>
            </div>
            <div v-else class="flex flex-column align-items-center mt-5" style="width: 42%; min-width: 32rem !important;">
                <SvgSuccess class="mb-4" className="registerconfirm-svg" color="var(--mysuccess-color)"/>
                <span class="text-center" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">{{ successMessage }}</span>

                <span v-if="successInfo" class="text-center mt-2" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">
                    <span v-if="successInfo.user_info" style="font-weight: var(--font-weight-bold);">{{ successInfo.user_info.first_name ?? "." }} {{ successInfo.user_info.last_name ?? "." }} ({{ successInfo.user_info.email ?? "." }})</span>
                    <span v-else>The requesting user</span>
                    now has the authorization to access and use the following
                    <span v-if="successInfo.serial_numbers && successInfo.serial_numbers.length>1">serial numbers</span>
                    <span v-else>serial number</span>
                    :
                    <span v-if="successInfo.serial_numbers" style="font-weight: var(--font-weight-bold);">{{ successInfo.serial_numbers.join(', ') }}</span>
                    <span v-else style="font-style: italic;">*Issue retrieving the serial numbers*</span>
                </span>

                <span style="font-size: var(--title-font-size); font-weight: var(--text-font-weight); gap: 0.4rem;" class="text-center mt-6 align-items-center">
                    If you did not want to share the access to this user, you can manually remove it from the 'Authorized Users' section in 
                    <router-link unstyled :to="{ path: '/login'}" style="text-decoration: none;">
                        <span id="clickable-text" class="cursor-pointer">Softron License Manager</span>
                    </router-link>.
                </span>
            </div>

        </div>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, watchEffect, onMounted } from 'vue';
// PrimeVue
import SvgSuccess from '@/components/SVGs/SvgSuccess.vue'
import SvgError from '@/components/SVGs/SvgError.vue'
// vue
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
// Others
import { useRouter } from 'vue-router'
import toastService from '@/composables/toastService';

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchAccessSharingConfirm } = useApiRequest()
const httpAccessSharingConfirmLoading = ref(false)
const httpAccessSharingConfirmData = ref(null)

const router = useRouter()

// --- PROPS ---
const props = defineProps({
    invitation: String
})
const { invitation } = toRefs(props)

// --- REDIRECT IF NO ATTACHED TOKEN ---
watchEffect(() => {
    if (!invitation.value) { router.push({ path: '/login' }) }
})

// --- VARIABLES ---
const isConfirmed = ref(false)
const errorConfirmation = ref(false)

// --- CONFIRMATION ---
const successMessage = ref("The access sharing has been successfully confirmed.")
const successInfo = ref(null)
const confirmationErrorMessage = ref(null)
const confirm = async () => {
    confirmationErrorMessage.value = null
    isConfirmed.value = false
    errorConfirmation.value = false
    // Send HTTP request
    httpAccessSharingConfirmLoading.value = true
    try {
        httpAccessSharingConfirmData.value = await fetchAccessSharingConfirm({ invitation: invitation.value })
        isConfirmed.value = true
        errorConfirmation.value = false
        successInfo.value = {
            user_info: httpAccessSharingConfirmData.value.user_info,
            serial_numbers: httpAccessSharingConfirmData.value.serial_numbers,
        }

        // HERE, we should store info about the authorized user info and serial number: to be shown then
    } catch (err) {
        handleAccessSharingConfirmError(err)
    } finally {
        httpAccessSharingConfirmLoading.value = false
    }
}
// Handle an error from the server
const handleAccessSharingConfirmError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Access Sharing Confirmation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        errorConfirmation.value = true
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        // 400: MISSING_OBJ / MISSING_OBJ_ELEM
        // 401: INVITATION_TOKEN_INVALID
        // 410: INVITATION_TOKEN_INVALID
        // 500: DATABASE_ERROR
        switch (errorStatus) {
            case 400: // Missing info = MISSING_OBJ / MISSING_OBJ_ELEM (400) => toast
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    confirmationErrorMessage.value = "It seems that the request body does not contain the necessary information."
                    //toastService.add({ severity: 'warn', summary: "Access Sharing Confirmation: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Access Sharing Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // INVITATION_TOKEN_INVALID (401) => toast
                if (errorCode == 'INVITATION_TOKEN_INVALID') {
                    confirmationErrorMessage.value = "It seems that the confirmation link is not valid anymore."
                    //toastService.add({ severity: 'warn', summary: "Access Sharing Confirmation: Invalid Confirmation.", detail: 'It seems that the confirmation link is not valid anymore.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Access Sharing Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 410: // INVITATION_TOKEN_INVALID (410) => toast
                if (errorCode == 'INVITATION_TOKEN_INVALID') {
                    confirmationErrorMessage.value = "It seems that the confirmation link contains information that is not up to date."
                    //toastService.add({ severity: 'warn', summary: "Access Sharing Confirmation: Invalid Confirmation.", detail: 'It seems that the confirmation link contains information that is not up to date.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Access Sharing Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR') {
                    confirmationErrorMessage.value = "Sorry, it seems that there is an internal error. Try later."
                    //toastService.add({ severity: 'error', summary: "Access Sharing Confirmation: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Access Sharing Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Access Sharing Confirmation: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Access Sharing Confirmation: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- Make the access_sharing_confirm request on mounted ! ---
onMounted(() => {
    confirm()
})
</script>

<style scoped>
:deep(.p-progress-spinner-circle) {
    stroke: var(--myprimary-color);
    animation: p-progress-spinner-dash 1.5s ease-in-out infinite !important;
}
/* Redirect */
#clickable-text {
    text-decoration: none;
    color: var(--button-normal-color);
}
#clickable-text:visited {
    color: var(--button-normal-color);
}
#clickable-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>